import { useCallback, useEffect, useRef } from "react";

import { useLocalStorage } from "./useLocalstorage";

interface ExpirableValue<T> {
  value: T;
  expiresAt: number; // Expiry timestamp in milliseconds
}

type ExpirableMap<T> = Record<string, ExpirableValue<T>>;

interface UseExpirableLocalStorageReturn<T> {
  /**
   * Retrieves a value for a given key.
   * @param key The key to retrieve.
   * @param defaultValue The value to return if the key is not found or has expired.
   */
  getValue: (key: string, defaultValue?: T) => T | undefined;
  /**
   * Sets a value for a given key with a specific TTL.
   * @param key The key to set.
   * @param value The value to store.
   * @param ttl The time-to-live in milliseconds. Must be a positive number.
   */
  setValue: (key: string, value: T, ttl: number) => void;
  /**
   * Removes a value for a given key from storage.
   * @param key The key to remove.
   */
  removeValue: (key: string) => void;
}

/**
 * A hook to manage multiple values under a single localStorage key, with individual TTL for each value.
 * Expired items are automatically cleaned up.
 *
 * @param namespace The key to use for localStorage, acting as a namespace for the stored items.
 *
 * @example
 * const { getValue, setValue } = useExpirableLocalStorage<{ acknowledged: boolean }>('shift-acknowledgements');
 *
 * // Set a value for a specific shift, expiring in 1 hour
 * setValue('shift-123', { acknowledged: true }, 3600 * 1000);
 *
 * // Get the value, providing a default if not found or expired
 * const ack = getValue('shift-123', { acknowledged: false });
 */
export function useExpirableLocalStorage<T>(namespace: string): UseExpirableLocalStorageReturn<T> {
  const [storedMap, setStoredMap] = useLocalStorage<ExpirableMap<T>>(namespace, {});

  const mapRef = useRef(storedMap);
  mapRef.current = storedMap ?? {};

  const cleanUpAndGetFreshMap = useCallback((): {
    freshMap: ExpirableMap<T>;
    isChanged: boolean;
  } => {
    const now = Date.now();
    const currentMap = mapRef.current;
    const newMap: ExpirableMap<T> = {};
    let isChanged = false;

    for (const key in currentMap) {
      if (Object.hasOwn(currentMap, key)) {
        const item = currentMap[key];
        if (item.expiresAt > now) {
          newMap[key] = item;
        } else {
          isChanged = true;
        }
      }
    }

    return { freshMap: newMap, isChanged };
  }, []);

  useEffect(() => {
    const { freshMap, isChanged } = cleanUpAndGetFreshMap();
    if (isChanged) {
      setStoredMap(freshMap);
    }
  }, [namespace, cleanUpAndGetFreshMap, setStoredMap]);

  const getValue = useCallback((key: string, defaultValue?: T): T | undefined => {
    const item = mapRef.current[key];

    if (item && item.expiresAt > Date.now()) {
      return item.value;
    }

    return defaultValue;
  }, []);

  const setValue = useCallback(
    (key: string, value: T, ttl: number) => {
      const { freshMap } = cleanUpAndGetFreshMap();
      const expiresAt = Date.now() + ttl;

      const newMap = {
        ...freshMap,
        [key]: { value, expiresAt },
      };

      setStoredMap(newMap);
    },
    [cleanUpAndGetFreshMap, setStoredMap]
  );

  const removeValue = useCallback(
    (key: string) => {
      const currentMap = mapRef.current;

      if (key in currentMap) {
        const newMap = { ...currentMap };
        delete newMap[key];
        setStoredMap(newMap);
      }
    },
    [setStoredMap]
  );

  return { getValue, setValue, removeValue };
}
